<template>
  <div class="container-fluid mt-4" :class="themeClass">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0">
          <i class="bi bi-clock-history me-2"></i>
          Historial de Sesiones de Caja
        </h5>
        <router-link to="/cajas" class="btn btn-success btn-sm">
          <i class="bi bi-cash-register me-1"></i> Ir a Caja
        </router-link>
      </div>

      <div class="card-body">
        <!-- Filtros -->
        <div class="row g-2 mb-4">
          <div class="col-md-3">
            <label class="form-label small">Caja</label>
            <select v-model="filtros.caja_id" class="form-select form-select-sm" @change="cargar">
              <option value="">Todas</option>
              <option v-for="c in cajas" :key="c.id" :value="c.id">{{ c.nombre_caja }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small">Estado</label>
            <select v-model="filtros.estado" class="form-select form-select-sm" @change="cargar">
              <option value="">Todos</option>
              <option value="abierta">Abierta</option>
              <option value="cerrada">Cerrada</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small">Desde</label>
            <input v-model="filtros.desde" type="date" class="form-control form-control-sm" @change="cargar" />
          </div>
          <div class="col-md-2">
            <label class="form-label small">Hasta</label>
            <input v-model="filtros.hasta" type="date" class="form-control form-control-sm" @change="cargar" />
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <button class="btn btn-primary btn-sm w-100" @click="cargar">
              <i class="bi bi-search me-1"></i> Buscar
            </button>
          </div>
        </div>

        <!-- Cargando -->
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-2 text-muted">Cargando sesiones...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="alert alert-danger">
          <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
        </div>

        <!-- Tabla -->
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th>#</th>
                <th>Caja</th>
                <th>Operador</th>
                <th>Apertura</th>
                <th>Cierre</th>
                <th class="text-end">Saldo Apertura</th>
                <th class="text-end">Ingresos</th>
                <th class="text-end">Egresos</th>
                <th class="text-end">Saldo Actual</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="sesiones.length === 0">
                <td colspan="11" class="text-center py-4 text-muted">
                  <i class="bi bi-inbox me-2"></i>No se encontraron sesiones
                </td>
              </tr>
              <tr v-for="s in sesiones" :key="s.id">
                <td class="text-muted small">{{ s.id }}</td>
                <td class="fw-semibold">{{ s.nombre_caja }}</td>
                <td>{{ s.nombre_usuario || '—' }}</td>
                <td class="small">{{ formatearFecha(s.fecha_apertura) }}</td>
                <td class="small">{{ s.fecha_cierre ? formatearFecha(s.fecha_cierre) : '—' }}</td>
                <td class="text-end">${{ fmt(s.saldo_apertura) }}</td>
                <td class="text-end text-success">+${{ fmt(s.total_ingresos) }}</td>
                <td class="text-end text-danger">-${{ fmt(s.total_egresos) }}</td>
                <td class="text-end fw-bold text-primary">${{ fmt(parseFloat(s.saldo_apertura) + parseFloat(s.saldo_movimientos)) }}</td>
                <td>
                  <span :class="s.estado === 'abierta' ? 'badge bg-success' : 'badge bg-secondary'">
                    {{ s.estado }}
                  </span>
                </td>
                <td>
                  <router-link :to="'/cajas/sesiones/' + s.id" class="btn btn-sm btn-outline-primary">
                    <i class="bi bi-eye"></i>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <nav v-if="totalPaginas > 1" class="mt-3">
          <ul class="pagination pagination-sm justify-content-center">
            <li class="page-item" :class="{ disabled: pagina === 1 }">
              <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina - 1)">Anterior</a>
            </li>
            <li
              v-for="p in totalPaginas"
              :key="p"
              class="page-item"
              :class="{ active: p === pagina }"
            >
              <a class="page-link" href="#" @click.prevent="cambiarPagina(p)">{{ p }}</a>
            </li>
            <li class="page-item" :class="{ disabled: pagina === totalPaginas }">
              <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina + 1)">Siguiente</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'

export default {
  name: 'ClientCajasListado',

  data() {
    return {
      cargando:     false,
      error:        null,
      sesiones:     [],
      cajas:        [],
      total:        0,
      pagina:       1,
      limite:       20,
      totalPaginas: 1,
      filtros: {
        caja_id: '',
        estado:  '',
        desde:   '',
        hasta:   '',
      },
    }
  },

  computed: {
    themeClass() { return themeManager.themeClass },
  },

  async created() {
    await Promise.all([this.cargarCajas(), this.cargar()])
  },

  methods: {
    async cargarCajas() {
      try {
        const { data } = await api.get('/cajas')
        this.cajas = data.cajas || []
      } catch { /* silencioso */ }
    },

    async cargar() {
      this.cargando = true
      this.error    = null
      try {
        const params = {
          pagina:  this.pagina,
          limite:  this.limite,
          ...Object.fromEntries(Object.entries(this.filtros).filter(([, v]) => v !== '')),
        }
        const { data } = await api.get('/cajas/sesiones', { params })
        this.sesiones     = data.sesiones     || []
        this.total        = data.total        || 0
        this.totalPaginas = data.paginas      || 1
      } catch {
        this.error = 'Error al cargar historial de sesiones'
      } finally {
        this.cargando = false
      }
    },

    cambiarPagina(p) {
      if (p >= 1 && p <= this.totalPaginas) {
        this.pagina = p
        this.cargar()
      }
    },

    fmt(val) { return parseFloat(val || 0).toFixed(2) },

    formatearFecha(fecha) {
      if (!fecha) return '—'
      return new Date(fecha).toLocaleString('es-EC', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>
